/**
 * OnboardingFlow — 3-screen emotional entry experience
 *
 * This is NOT onboarding. It is the first moment of a reset.
 * Calm, spacious, different from a typical app.
 *
 * Screen 1: ARRIVAL — "Reclaim your inner world"
 * Screen 2: RECOGNITION — "Most people try to change their life… from a stressed system"
 * Screen 3: FOUNDATION — "First, we change your state"
 *
 * Shows once on first visit. Replay from settings.
 * Nature imagery with heavy blur/overlay — felt atmosphere, not literal scenes.
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SCREENS = [
  {
    id: 'arrival',
    image: 'https://images.unsplash.com/photo-1771652108276-43afdec67e58?w=1280&q=80&auto=format',
    overlay: 'rgba(12,10,8,0.55)',
    warmth: 'radial-gradient(ellipse 80% 70% at 50% 45%, rgba(180,140,80,0.06) 0%, transparent 60%)',
  },
  {
    id: 'recognition',
    image: 'https://images.unsplash.com/photo-1771250851316-c7abe348fb0c?w=1280&q=80&auto=format',
    overlay: 'rgba(8,8,12,0.65)',
    warmth: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(120,130,160,0.04) 0%, transparent 55%)',
  },
  {
    id: 'foundation',
    image: 'https://images.unsplash.com/photo-1760891489096-46da9fdcd8a7?w=1280&q=80&auto=format',
    overlay: 'rgba(10,10,8,0.5)',
    warmth: 'radial-gradient(ellipse 85% 75% at 50% 40%, rgba(200,170,100,0.07) 0%, transparent 55%)',
  },
];

const OnboardingFlow = ({ onComplete }) => {
  const [screen, setScreen] = useState(0);
  const [phase, setPhase] = useState('enter');
  const [breathDone, setBreathDone] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Preload all images
  useEffect(() => {
    let loaded = 0;
    SCREENS.forEach(s => {
      const img = new Image();
      img.onload = () => { loaded++; if (loaded === SCREENS.length) setImagesLoaded(true); };
      img.onerror = () => { loaded++; if (loaded === SCREENS.length) setImagesLoaded(true); };
      img.src = s.image;
    });
    // Fallback if images take too long
    const t = setTimeout(() => setImagesLoaded(true), 4000);
    return () => clearTimeout(t);
  }, []);

  // Breathing animation on Screen 1 before CTA
  useEffect(() => {
    if (screen === 0 && phase === 'enter') {
      const t = setTimeout(() => setBreathDone(true), 5500);
      return () => clearTimeout(t);
    }
  }, [screen, phase]);

  const handleNext = useCallback(() => {
    setPhase('exit');
    setTimeout(() => {
      if (screen < 2) {
        setScreen(s => s + 1);
        setPhase('enter');
      } else {
        localStorage.setItem('reclaim_onboarding_seen', 'true');
        onComplete();
      }
    }, 800);
  }, [screen, onComplete]);

  const handleSkip = useCallback(() => {
    localStorage.setItem('reclaim_onboarding_seen', 'true');
    onComplete();
  }, [onComplete]);

  if (!imagesLoaded) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-[9999]">
        <motion.div
          className="w-1 h-1 rounded-full bg-white/20"
          animate={{ scale: [1, 3, 1], opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
      </div>
    );
  }

  const current = SCREENS[screen];

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden" data-testid="onboarding-flow">
      {/* Background image — blurred, overlaid, slow Ken Burns drift */}
      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2 }}
        >
          <motion.div
            className="absolute inset-0"
            animate={{
              scale: [1, 1.06],
              x: [0, screen === 1 ? -10 : 8],
            }}
            transition={{ duration: 25, ease: 'linear' }}
            style={{
              backgroundImage: `url(${current.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(8px) saturate(0.7) brightness(0.7)',
            }}
          />
          {/* Dark overlay */}
          <div className="absolute inset-0" style={{ background: current.overlay }} />
          {/* Warm tint */}
          <div className="absolute inset-0" style={{ background: current.warmth }} />
          {/* Soft vignette */}
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse 70% 65% at 50% 50%, transparent 0%, rgba(0,0,0,0.4) 100%)',
          }} />
        </motion.div>
      </AnimatePresence>

      {/* Skip button */}
      <motion.button
        onClick={handleSkip}
        className="absolute top-8 right-8 z-50 text-white/15 text-xs tracking-widest uppercase"
        style={{ fontFamily: "'Manrope', sans-serif" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        data-testid="onboarding-skip-btn"
      >
        Skip
      </motion.button>

      {/* Progress dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-50">
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className="w-1.5 h-1.5 rounded-full transition-all duration-500"
            style={{
              background: i === screen ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.1)',
              width: i === screen ? 20 : 6,
              borderRadius: 10,
            }}
            data-testid={`onboarding-dot-${i}`}
          />
        ))}
      </div>

      {/* Screen content */}
      <AnimatePresence mode="wait">
        {screen === 0 && (
          <ScreenArrival
            key="arrival"
            phase={phase}
            breathDone={breathDone}
            onNext={handleNext}
          />
        )}
        {screen === 1 && (
          <ScreenRecognition
            key="recognition"
            phase={phase}
            onNext={handleNext}
          />
        )}
        {screen === 2 && (
          <ScreenFoundation
            key="foundation"
            phase={phase}
            onNext={handleNext}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── SCREEN 1: ARRIVAL ─── */
function ScreenArrival({ phase, breathDone, onNext }) {
  return (
    <motion.div
      className="absolute inset-0 flex flex-col items-center justify-center px-8 z-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: phase === 'exit' ? 0 : 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
    >
      <div className="max-w-lg text-center">
        {/* Headline */}
        <motion.h1
          className="text-white/80 text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 1.2 }}
          data-testid="onboarding-arrival-headline"
        >
          Reclaim your inner world
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          className="text-white/35 text-sm sm:text-base leading-relaxed mb-5 max-w-sm mx-auto"
          style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.02em' }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8, duration: 1.2 }}
        >
          Tools and immersive experiences to help you regulate your system, rewire old patterns, and create from within.
        </motion.p>

        {/* Micro line */}
        <motion.p
          className="text-white/20 text-xs tracking-wide mb-12"
          style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.06em' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.8, duration: 1 }}
        >
          Not more effort. A different way.
        </motion.p>

        {/* Breathing animation before CTA */}
        <motion.div
          className="mx-auto mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: breathDone ? 0 : 1 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="w-10 h-10 rounded-full mx-auto"
            style={{ border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{ duration: 5, ease: 'easeInOut' }}
          />
        </motion.div>

        {/* CTA — appears after breath */}
        {breathDone && (
          <motion.button
            onClick={onNext}
            className="text-white/45 text-base tracking-wide"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ opacity: { duration: 4, repeat: Infinity, ease: 'easeInOut' }, y: { duration: 0 } }}
            whileTap={{ scale: 0.96 }}
            data-testid="onboarding-enter-btn"
          >
            Enter
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

/* ─── SCREEN 2: RECOGNITION ─── */
function ScreenRecognition({ phase, onNext }) {
  const [visibleLines, setVisibleLines] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setVisibleLines(1), 800),
      setTimeout(() => setVisibleLines(2), 2000),
      setTimeout(() => setVisibleLines(3), 3200),
      setTimeout(() => setVisibleLines(4), 4400),
      setTimeout(() => setVisibleLines(5), 6200),
      setTimeout(() => setVisibleLines(6), 8000),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <motion.div
      className="absolute inset-0 flex flex-col items-center justify-center px-8 z-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: phase === 'exit' ? 0 : 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
    >
      <div className="max-w-md text-center">
        {/* Headline */}
        <motion.h1
          className="text-white/75 text-2xl sm:text-3xl lg:text-4xl leading-tight mb-10"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: visibleLines >= 1 ? 1 : 0, y: visibleLines >= 1 ? 0 : 12 }}
          transition={{ duration: 1.2 }}
          data-testid="onboarding-recognition-headline"
        >
          Most people try to change their life{'\u2026'} from a stressed system
        </motion.h1>

        {/* Supporting lines — fade in one by one */}
        <div className="space-y-3 mb-8">
          {['Overthinking', 'Pressure', 'Old patterns repeating'].map((text, i) => (
            <motion.p
              key={text}
              className="text-white/30 text-sm"
              style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.04em' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: visibleLines >= i + 2 ? 1 : 0 }}
              transition={{ duration: 1 }}
            >
              {text}
            </motion.p>
          ))}
        </div>

        {/* Pause + "That's why it doesn't last" */}
        <motion.p
          className="text-white/25 text-xs mb-10"
          style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.05em' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: visibleLines >= 5 ? 1 : 0 }}
          transition={{ duration: 1.2 }}
        >
          That{'\u2019'}s why it doesn{'\u2019'}t last
        </motion.p>

        {/* Transition line */}
        <motion.p
          className="text-white/45 text-base mb-10"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: visibleLines >= 6 ? 1 : 0, y: visibleLines >= 6 ? 0 : 8 }}
          transition={{ duration: 1.2 }}
          data-testid="onboarding-recognition-transition"
        >
          Reclaim works differently
        </motion.p>

        {/* CTA */}
        {visibleLines >= 6 && (
          <motion.button
            onClick={onNext}
            className="text-white/40 text-sm tracking-wide"
            style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.06em' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            whileTap={{ scale: 0.96 }}
            data-testid="onboarding-continue-btn"
          >
            Continue
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

/* ─── SCREEN 3: FOUNDATION ─── */
function ScreenFoundation({ phase, onNext }) {
  const [visibleLines, setVisibleLines] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setVisibleLines(1), 800),
      setTimeout(() => setVisibleLines(2), 2200),
      setTimeout(() => setVisibleLines(3), 3400),
      setTimeout(() => setVisibleLines(4), 4600),
      setTimeout(() => setVisibleLines(5), 6400),
      setTimeout(() => setVisibleLines(6), 8000),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <motion.div
      className="absolute inset-0 flex flex-col items-center justify-center px-8 z-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: phase === 'exit' ? 0 : 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
    >
      <div className="max-w-md text-center">
        {/* Headline */}
        <motion.h1
          className="text-white/80 text-2xl sm:text-3xl lg:text-4xl leading-tight mb-10"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: visibleLines >= 1 ? 1 : 0, y: visibleLines >= 1 ? 0 : 12 }}
          transition={{ duration: 1.2 }}
          data-testid="onboarding-foundation-headline"
        >
          First, we change your state
        </motion.h1>

        {/* Supporting lines */}
        <div className="space-y-3 mb-8">
          {['Calm the system', 'Open the mind', 'Create from a different place'].map((text, i) => (
            <motion.p
              key={text}
              className="text-white/35 text-sm"
              style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.04em' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: visibleLines >= i + 2 ? 1 : 0 }}
              transition={{ duration: 1 }}
            >
              {text}
            </motion.p>
          ))}
        </div>

        {/* Closing line */}
        <motion.p
          className="text-white/30 text-xs mb-12"
          style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.05em' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: visibleLines >= 5 ? 1 : 0 }}
          transition={{ duration: 1.2 }}
        >
          From here, everything begins to open
        </motion.p>

        {/* CTA */}
        {visibleLines >= 6 && (
          <motion.button
            onClick={onNext}
            className="text-white/50 text-base"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: [0.35, 0.6, 0.35] }}
            transition={{ opacity: { duration: 4, repeat: Infinity, ease: 'easeInOut' } }}
            whileTap={{ scale: 0.96 }}
            data-testid="onboarding-begin-btn"
          >
            Begin
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

export default OnboardingFlow;
